import styled, { createGlobalStyle } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { greenMui, greyMui, orangeMui, rydeColors } from "@ryde/theme";

import {
  LIVE_MAP_INCIDENT_BADGE_UNOPENED_SCALE_MIN,
  LIVE_MAP_INCIDENT_ICON_CANVAS_PX,
  LIVE_MAP_INCIDENT_MARKER_BOX_PX,
  LIVE_MAP_INCIDENT_OPENED_HALO_SIZE_PX,
} from "./parts";

/** Leaflet `DivIcon` HTML uses plain class names; styles must be global. */
export const LiveMapIncidentMarkerGlobalStyle = createGlobalStyle`
  @keyframes ryde-live-map-incident-pulse-kf {
    0% {
      transform: scale(0.6);
      opacity: 1;
    }
    100% {
      transform: scale(2.55);
      opacity: 0;
    }
  }

  @keyframes ryde-live-map-incident-badge-scale-kf {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(${LIVE_MAP_INCIDENT_BADGE_UNOPENED_SCALE_MIN});
    }
  }

  .ryde-live-map-incident-marker-canvas {
    position: relative;
    width: ${LIVE_MAP_INCIDENT_ICON_CANVAS_PX}px;
    height: ${LIVE_MAP_INCIDENT_ICON_CANVAS_PX}px;
    overflow: visible;
    pointer-events: none;
  }

  .ryde-live-map-incident-marker-canvas .ryde-live-map-incident-marker {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    pointer-events: auto;
  }

  .ryde-live-map-incident-marker {
    position: relative;
    width: ${LIVE_MAP_INCIDENT_MARKER_BOX_PX}px;
    height: ${LIVE_MAP_INCIDENT_MARKER_BOX_PX}px;
    overflow: visible;
  }

  .ryde-live-map-incident-marker__halo {
    display: none;
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${LIVE_MAP_INCIDENT_OPENED_HALO_SIZE_PX}px;
    height: ${LIVE_MAP_INCIDENT_OPENED_HALO_SIZE_PX}px;
    margin: 0;
    transform: translate(-50%, -52%);
    border-radius: 50%;
    z-index: 0;
    pointer-events: none;
  }

  .ryde-live-map-incident-marker--opened .ryde-live-map-incident-marker__halo {
    display: block;
  }

  .ryde-live-map-incident-marker__dot {
    position: absolute;
    top: 11px;
    left: 11px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${orangeMui._600};
    border: 2px solid ${rydeColors.white};
    box-sizing: border-box;
    z-index: 2;
    box-shadow: 0 1px 3px ${rydeColors.black}40;
  }

  .ryde-live-map-incident-marker__pulse {
    display: none;
    position: absolute;
    top: 3px;
    left: 3px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    box-sizing: border-box;
    z-index: 1;
    pointer-events: none;
  }

  .ryde-live-map-incident-marker--unopened .ryde-live-map-incident-marker__pulse {
    display: block;
    animation: ryde-live-map-incident-pulse-kf 1.35s ease-out infinite;
  }

  .ryde-live-map-incident-marker--opened .ryde-live-map-incident-marker__pulse {
    display: block;
    top: 50%;
    left: 50%;
    width: 30px;
    height: 30px;
    margin: 0;
    transform: translate(-50%, -50%);
    opacity: 1;
    animation: none;
    background: transparent;
    z-index: 1;
  }

  .ryde-live-map-incident-marker--unopened-typed
    .ryde-live-map-incident-marker__badge {
    animation: ryde-live-map-incident-badge-scale-kf 1.25s ease-in-out infinite;
    transform-origin: center center;
  }

  .ryde-live-map-incident-marker--typed .ryde-live-map-incident-marker__badge {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 30px;
    height: 30px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    background: transparent;
    border: none;
    box-shadow: none;
  }

  .ryde-live-map-incident-marker--typed .ryde-live-map-incident-marker__badge svg {
    display: block;
    overflow: visible;
  }

`;

/** Tooltip body (FramerTooltip portal): keep styles here, not under `IncidentPopupRoot`. */
export const LiveMapIncidentTooltipStackStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: start;
  line-height: 1.35;
  max-width: 260px;
`;

/** Type color swatch in live-map incident popup (matches ride drawer incidents list dot). */
export const LiveMapIncidentTypeColorDotStyled = styled.span.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "live-map-incident-type-color-dot",
  },
)<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid ${greyMui._300};
  background-color: ${p => p.$color};
`;

export const IncidentPopupRoot = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "live-map-incident-popup",
})`
  min-width: 220px;
  max-width: 280px;
  font-family: inherit;
  background: ${rydeColors.white};
  border: 1px solid ${greyMui._300};
  border-radius: 6px;
  padding: 0;
  margin: 0;
  overflow: hidden;

  .ryde-live-map-incident-popup__title {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: stretch;
    padding: 12px 36px 10px 12px;
    border-bottom: 1px solid ${greyMui._200};
  }

  .ryde-live-map-incident-popup__type-row {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .ryde-live-map-incident-popup__type-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
    cursor: default;
  }

  .ryde-live-map-incident-popup__type-name {
    font-weight: 700;
    font-size: 15px;
    line-height: 1.3;
    color: ${rydeColors.black};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ryde-live-map-incident-popup__creator {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.35;
    color: ${greyMui._700};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: help;
  }

  .ryde-live-map-incident-popup__body {
    padding: 10px 12px 12px;
  }

  .ryde-live-map-incident-popup__row {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    margin-bottom: 8px;
    font-size: 13px;
    line-height: 1.35;
  }

  .ryde-live-map-incident-popup__row:last-of-type {
    margin-bottom: 0;
  }

  .ryde-live-map-incident-popup__label {
    color: ${greyMui._600};
    flex: 0 0 auto;
  }

  .ryde-live-map-incident-popup__value {
    color: ${rydeColors.black};
    font-weight: 500;
  }

  .ryde-live-map-incident-popup__value--driver-hint {
    cursor: help;
  }

  .ryde-live-map-incident-popup__footer {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 8px;
    margin: 4px 8px 10px;
  }

  .ryde-live-map-incident-popup__close {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    padding: 10px 14px;
    border: none;
    border-radius: 5px;
    background: ${greenMui._500};
    color: ${rydeColors.white};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .ryde-live-map-incident-popup__close:hover:not(:disabled) {
    background: ${greenMui._600};
  }

  .ryde-live-map-incident-popup__close:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .ryde-live-map-incident-popup__open {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    min-height: 42px;
    padding: 0;
    border: 1px solid ${greyMui._300};
    border-radius: 5px;
    background: ${rydeColors.white};
    color: ${greyMui._800};
    cursor: pointer;
    transition:
      background 0.15s ease,
      border-color 0.15s ease;
  }

  .ryde-live-map-incident-popup__open:hover:not(:disabled) {
    background: ${greyMui._50};
    border-color: ${greyMui._400};
  }

  .ryde-live-map-incident-popup__open:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
